// src/components/resumo/annotations/AnnotationLayer.tsx
// 🎯 Camada de anotações - VERSÃO FINAL REFATORADA
import React, { useState, useEffect, useCallback, useRef } from 'react';
import UnifiedAnnotationToolbar from './UnifiedAnnotationToolbar';
import { CheckCircle, XCircle } from 'lucide-react';
import NotesSidebar from './NotesSidebar';
import NoteModal from './NoteModal';
import NoteQuizModal from './NoteQuizModal';
import HighlightCommentIcon from './HighlightCommentIcon';
import CommentsMargin, { CommentInputModal } from './CommentsMargin';
import { useHighlight } from './useHighlight';
import { AnnotationData, Comment, Note, NoteCategory } from './annotations.types';
import './UnifiedAnnotationToolbar.css';
import './NotesSidebar.css';
import './NoteModal.css';
import './NoteQuizModal.css';
import './HighlightCommentIcon.css';
import './comments.css';
import UnsavedChangesModal from './UnsavedChangesModal';
import './UnsavedChangesModal.css';
import { initCommentTooltips } from './commentTooltip';

interface AnnotationLayerProps {
    pageId: string;
    annotations: AnnotationData;
    onAddHighlight: (highlight: any) => void;
    onRemoveHighlight: (id: string) => void;
    onAddNote: (note: Note) => void;
    onUpdateNote: (id: string, data: Partial<Note>) => void;
    onUpdateHighlightNote: (highlightId: string, note: string) => void;
    onRemoveNote: (id: string) => void;
    onAddComment: (comment: Comment) => void;
    onRemoveComment: (id: string) => void;
    onUpdateComment: (id: string, data: Partial<Comment>) => void;
    onSave: () => Promise<{ success: boolean; message: string }>;
    onClose: () => void;
    onDirtyChange?: (dirty: boolean) => void;
    darkMode?: boolean;
    onFirstHighlightMade?: (highlightId?: string) => void;
    onFirstNoteMade?: () => void;
    contentRef?: React.RefObject<HTMLElement | null>;
    authorName?: string;
    // Undo/Redo
    onUndo?: () => void;
    onRedo?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
}

const AnnotationLayer: React.FC<AnnotationLayerProps> = ({
    pageId,
    annotations,
    onAddHighlight,
    onRemoveHighlight,
    onAddNote,
    onUpdateNote,
    onUpdateHighlightNote,
    onRemoveNote,
    onAddComment,
    onRemoveComment,
    onUpdateComment,
    onSave,
    onClose,
    onDirtyChange,
    onFirstHighlightMade,
    onFirstNoteMade,
    darkMode = false,
    contentRef,
    authorName = 'Eu',
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
}) => {
    const [isDirty, setIsDirty] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const firstHighlightFired = useRef(false);

    // Atalhos de teclado: Ctrl+Z / Ctrl+Y
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) return;
            if (e.key === 'z' && !e.shiftKey && canUndo && onUndo) {
                e.preventDefault();
                onUndo();
            }
            if ((e.key === 'y' || (e.key === 'z' && e.shiftKey)) && canRedo && onRedo) {
                e.preventDefault();
                onRedo();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [canUndo, canRedo, onUndo, onRedo]);

    // ✅ Ref para o callback do onboarding — evita dependência circular
    const onFirstHighlightMadeRef = useRef(onFirstHighlightMade);
    useEffect(() => {
        onFirstHighlightMadeRef.current = onFirstHighlightMade;
    }, [onFirstHighlightMade]);

    // ✅ Wrapper definido ANTES do useHighlight para ser passado a ele
    const handleAddHighlight = useCallback((highlight: any) => {
        onAddHighlight(highlight);
        if (!firstHighlightFired.current && onFirstHighlightMadeRef.current) {
            firstHighlightFired.current = true;
            onFirstHighlightMadeRef.current(highlight.id);
        }
    }, [onAddHighlight]);

    // ✅ Hook de grifos — recebe handleAddHighlight (não onAddHighlight direto)
    const {
        isHighlighting,
        setIsHighlighting,
        isRemoving,
        setIsRemoving,
        currentColor,
        setCurrentColor,
        currentThickness,
        setCurrentThickness,
        currentStyle,
        setCurrentStyle
    } = useHighlight(handleAddHighlight, onRemoveHighlight, pageId, setIsDirty);

    // ✅ Estados de notas
    const [activeNoteCategory, setActiveNoteCategory] = useState<NoteCategory | null>(null);
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | undefined>();
    const [quizModalOpen, setQuizModalOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // ✅ Estados de comentário em grifo
    const [commentHighlightId, setCommentHighlightId] = useState<string | null>(null);

    // ✅ Estados de comentários de margem (Word-style)
    const [isCommenting, setIsCommenting] = useState(false);
    const [showComments, setShowComments] = useState(true);
    const [activeCommentId, setActiveCommentId] = useState<string | null>(null);

    useEffect(() => {
        if (showComments) {
            document.body.classList.remove('comments-hidden');
        } else {
            document.body.classList.add('comments-hidden');
        }
        return () => { document.body.classList.remove('comments-hidden'); };
    }, [showComments]);
    const [pendingComment, setPendingComment] = useState<{
        selectedText: string;
        position: { x: number; y: number };
        range: Range;
    } | null>(null);

    // ✅ Handler: Toggle categoria de nota
    const handleToggleNoteCategory = (category: NoteCategory) => {
        if (activeNoteCategory === category) {
            setActiveNoteCategory(null);
        } else {
            setActiveNoteCategory(category);
            setIsHighlighting(false);
            setIsRemoving(false);
            setIsDirty(true)
        }
    };

    // ✅ Handler: Criar nota livre
    const handleCreateFreeNote = () => {
        setEditingNote(undefined);
        setNoteModalOpen(true);
        setIsDirty(true);
    };

    // ✅ ADICIONAR: Notificar parent quando isDirty mudar
    useEffect(() => {
        if (onDirtyChange) {
            onDirtyChange(isDirty);
        }
    }, [isDirty, onDirtyChange]);

    // ✅ Inicializar tooltips de comentário
    useEffect(() => {
        initCommentTooltips();
    }, []);

    // ✅ Handler: Criar nota ao selecionar texto (quando categoria ativa)
    React.useEffect(() => {
        let mobileNoteTooltip: HTMLElement | null = null;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        const removeMobileNoteTooltip = () => {
            if (mobileNoteTooltip) {
                mobileNoteTooltip.remove();
                mobileNoteTooltip = null;
            }
        };

        if (!activeNoteCategory) {
            return () => {
                if (debounceTimer) clearTimeout(debounceTimer);
                removeMobileNoteTooltip();
            };
        }

        const isMobile = () => window.matchMedia('(pointer: coarse)').matches;

        // ========== DESKTOP: mouseup direto ==========
        const handleMouseUp = () => {
            if (isMobile()) return;

            const selection = window.getSelection();
            if (!selection || !selection.toString().trim()) return;

            const range = selection.getRangeAt(0);
            const contentEl = document.querySelector('.rrp-rendered-content');
            if (!contentEl?.contains(range.commonAncestorContainer)) return;

            const newNote: Note = {
                id: `note_${Date.now()}_${Math.random()}`,
                pageId,
                text: selection.toString().trim(),
                category: activeNoteCategory,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            onAddNote(newNote);
            selection.removeAllRanges();
            setSaveStatus({ message: `Nota "${activeNoteCategory}" criada!`, type: 'success' });
            setTimeout(() => setSaveStatus(null), 2000);
        };

        // ========== MOBILE: selectionchange + debounce (igual ao useHighlight) ==========
        const handleSelectionChange = () => {
            if (!isMobile()) return;

            if (debounceTimer) clearTimeout(debounceTimer);

            debounceTimer = setTimeout(() => {
                const selection = window.getSelection();
                const selectedText = selection?.toString().trim() || '';

                if (!selectedText || selectedText.length < 2) {
                    removeMobileNoteTooltip();
                    return;
                }

                const range = selection!.getRangeAt(0);
                const contentEl = document.querySelector('.rrp-rendered-content');
                if (!contentEl?.contains(range.commonAncestorContainer)) {
                    removeMobileNoteTooltip();
                    return;
                }

                document.querySelectorAll('.mobile-highlight-floating-btn').forEach(el => el.remove());
                removeMobileNoteTooltip();
                const rect = range.getBoundingClientRect();
                const tooltip = document.createElement('div');
                tooltip.className = `mobile-highlight-floating-btn${darkMode ? ' dark' : ''}`;

                const top = rect.top + window.scrollY - 52;
                const left = Math.min(
                    rect.left + rect.width / 2 - 70,
                    window.innerWidth - 150
                );

                tooltip.style.cssText = `
                position: absolute;
                top: ${Math.max(top, window.scrollY + 8)}px;
                left: ${Math.max(left, 8)}px;
                z-index: 99999;
            `;

                const savedText = selectedText;
                const savedCategory = activeNoteCategory;

                tooltip.innerHTML = `
                <button class="mhfb-note-btn" data-action="note">📝 Criar nota</button>
                <button class="mhfb-cancel-btn" data-action="cancel">✕</button>
            `;

                tooltip.addEventListener('pointerdown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const target = e.target as HTMLElement;
                    const action = target.closest('[data-action]')?.getAttribute('data-action');

                    if (action === 'note') {
                        const newNote: Note = {
                            id: `note_${Date.now()}_${Math.random()}`,
                            pageId,
                            text: savedText,
                            category: savedCategory,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        };
                        onAddNote(newNote);
                        setSaveStatus({ message: 'Nota criada!', type: 'success' });
                        setTimeout(() => setSaveStatus(null), 2000);
                    }

                    const sel = window.getSelection();
                    if (sel) sel.removeAllRanges();
                    removeMobileNoteTooltip();
                });

                document.body.appendChild(tooltip);
                mobileNoteTooltip = tooltip;

            }, 400); // mesmo debounce do useHighlight
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('selectionchange', handleSelectionChange);
            if (debounceTimer) clearTimeout(debounceTimer);
            removeMobileNoteTooltip();
        };
    }, [activeNoteCategory, pageId, onAddNote, darkMode]);

    // ✅ Handler: Clicar em nota no sidebar
    const handleNoteClick = (noteId: string) => {
        const note = annotations.notes.find(n => n.id === noteId);
        if (note) {
            setEditingNote(note);
            setNoteModalOpen(true);
        }
    };

    // ✅ Handler: Salvar nota
    const handleSaveNote = (data: Partial<Note>) => {
        if (editingNote) {
            onUpdateNote(editingNote.id, data);
        } else {
            // ✅ Nota livre (sem vínculo a texto selecionado)
            const newNote: Note = {
                id: `note_${Date.now()}_${Math.random()}`,
                pageId,
                text: data.text || '',
                category: data.category || 'importante',
                question: data.question,
                answer: data.answer,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            onAddNote(newNote);
        }
        setNoteModalOpen(false);
        setEditingNote(undefined);
        setIsDirty(true);
    };

    // ✅ Handler: Iniciar quiz
    const handleStartQuiz = () => {
        const quizNotes = annotations.notes.filter(n => n.pageId === pageId && n.question);
        if (quizNotes.length > 0) {
            setQuizModalOpen(true);
        }
    };

    // ✅ useEffect: Modo comentário — escutar seleção de texto
    useEffect(() => {
        if (!isCommenting) {
            document.body.classList.remove('comment-mode');
            return;
        }
        document.body.classList.add('comment-mode');

        const isMobile = () => window.matchMedia('(pointer: coarse)').matches;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let mobileBtn: HTMLElement | null = null;

        const removeBtn = () => {
            if (mobileBtn) { mobileBtn.remove(); mobileBtn = null; }
        };

        // Desktop: mouseup
        const handleMouseUp = (e: MouseEvent) => {
            if (isMobile()) return;
            const selection = window.getSelection();
            const text = selection?.toString().trim() || '';
            if (!text || text.length < 2) return;

            const range = selection!.getRangeAt(0);
            const contentEl = document.querySelector('.rrp-rendered-content');
            if (!contentEl?.contains(range.commonAncestorContainer)) return;

            e.stopPropagation();
            setPendingComment({
                selectedText: text,
                position: { x: e.clientX + 12, y: e.clientY - 20 },
                range: range.cloneRange(),
            });
        };

        // Mobile: selectionchange + debounce
        const handleSelectionChange = () => {
            if (!isMobile()) return;
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const selection = window.getSelection();
                const text = selection?.toString().trim() || '';
                if (!text || text.length < 2) { removeBtn(); return; }

                const range = selection!.getRangeAt(0);
                const contentEl = document.querySelector('.rrp-rendered-content');
                if (!contentEl?.contains(range.commonAncestorContainer)) { removeBtn(); return; }

                removeBtn();
                const rect = range.getBoundingClientRect();
                const btn = document.createElement('div');
                btn.className = `mobile-comment-floating-btn${darkMode ? ' dark' : ''}`;
                const top = Math.max(rect.top + window.scrollY - 52, window.scrollY + 8);
                const left = Math.min(rect.left + rect.width / 2 - 60, window.innerWidth - 130);
                btn.style.cssText = `position:absolute;top:${top}px;left:${Math.max(left,8)}px;z-index:99999;`;
                btn.innerHTML = `<button class="mhfb-comment-btn" data-action="comment">Comentar</button><button class="mhfb-cancel-btn" data-action="cancel">✕</button>`;

                const savedText = text;
                const savedRange = range.cloneRange();
                btn.addEventListener('pointerdown', (ev) => {
                    ev.preventDefault(); ev.stopPropagation();
                    const action = (ev.target as HTMLElement).closest('[data-action]')?.getAttribute('data-action');
                    if (action === 'comment') {
                        const r = savedRange.getBoundingClientRect();
                        setPendingComment({
                            selectedText: savedText,
                            position: { x: Math.min(r.right + 8, window.innerWidth - 290), y: r.top },
                            range: savedRange,
                        });
                    }
                    window.getSelection()?.removeAllRanges();
                    removeBtn();
                });
                document.body.appendChild(btn);
                mobileBtn = btn;
            }, 400);
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('selectionchange', handleSelectionChange);
            if (debounceTimer) clearTimeout(debounceTimer);
            removeBtn();
            document.body.classList.remove('comment-mode');
        };
    }, [isCommenting, darkMode]);

    // ✅ Handler: salvar comentário pendente
    const handleSaveComment = useCallback((text: string, title: string) => {
        if (!pendingComment) return;
        const { selectedText, range } = pendingComment;
        const commentId = `comment_${Date.now()}_${Math.random().toString(36).slice(2)}`;

        // Marcar o texto com span
        try {
            const mark = document.createElement('span');
            mark.className = 'comment-mark';
            mark.dataset.commentId = commentId;
            range.surroundContents(mark);
            mark.addEventListener('click', () => setActiveCommentId(commentId));
        } catch {
            // surroundContents falha se a seleção cruza elementos; ignorar e continuar
        }

        const comment: Comment = {
            id: commentId,
            pageId,
            selectedText,
            comment: text,
            title: title || undefined,
            createdAt: new Date().toISOString(),
        };
        onAddComment(comment);
        setIsDirty(true);
        setPendingComment(null);
        window.getSelection()?.removeAllRanges();
        setSaveStatus({ message: 'Comentário adicionado!', type: 'success' });
        setTimeout(() => setSaveStatus(null), 2000);
    }, [pendingComment, pageId, onAddComment]);

    // Remover o span do DOM ao deletar comentário
    const handleRemoveComment = useCallback((id: string) => {
        const span = document.querySelector(`[data-comment-id="${id}"]`);
        if (span && span.parentNode) {
            while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
            span.parentNode.removeChild(span);
        }
        onRemoveComment(id);
        setIsDirty(true);
    }, [onRemoveComment]);

    const handleUpdateComment = useCallback((id: string, data: Partial<Comment>) => {
        onUpdateComment(id, data);
        setIsDirty(true);
    }, [onUpdateComment]);

    // ✅ Re-aplicar marcadores de comentário quando o conteúdo é re-renderizado
    // (resolve bug: span some ao navegar para outra página e voltar)
    useEffect(() => {
        if (!annotations.comments?.length || !contentRef) return;

        const applyMarks = () => {
            const contentEl = contentRef.current;
            if (!contentEl) return;

            annotations.comments
                .filter(c => c.pageId === pageId)
                .forEach(comment => {
                    // Se o span já existe no DOM, não recriar
                    if (document.querySelector(`[data-comment-id="${comment.id}"]`)) return;

                    // Buscar o texto selecionado no conteúdo para re-marcar
                    const walker = document.createTreeWalker(
                        contentEl,
                        NodeFilter.SHOW_TEXT,
                        null
                    );

                    let node: Text | null;
                    const target = comment.selectedText.trim().slice(0, 60); // limite para busca
                    if (!target) return;

                    while ((node = walker.nextNode() as Text | null)) {
                        const idx = node.textContent?.indexOf(target) ?? -1;
                        if (idx !== -1) {
                            try {
                                const range = document.createRange();
                                range.setStart(node, idx);
                                range.setEnd(node, idx + target.length);
                                const mark = document.createElement('span');
                                mark.className = 'comment-mark';
                                mark.dataset.commentId = comment.id;
                                range.surroundContents(mark);
                                mark.addEventListener('click', () => setActiveCommentId(comment.id));
                            } catch {
                                // Ignorar se o range cruzar elementos
                            }
                            break; // marcar apenas a primeira ocorrência
                        }
                    }
                });
        };

        // Tentar imediatamente e com delay para aguardar renderização
        applyMarks();
        const t = setTimeout(() => {
            applyMarks();
            window.dispatchEvent(new CustomEvent('comment-marks-applied'));
        }, 400);
        return () => clearTimeout(t);
    }, [annotations.comments, pageId, contentRef]);

    // Re-aplicar marks quando highlights mudam (dangerouslySetInnerHTML destrói spans)
    useEffect(() => {
        if (!annotations.comments?.length || !contentRef) return;
        const t = setTimeout(() => {
            const contentEl = contentRef.current;
            if (!contentEl) return;
            annotations.comments
                .filter(c => c.pageId === pageId)
                .forEach(comment => {
                    if (document.querySelector(`[data-comment-id="${comment.id}"]`)) return;
                    const walker = document.createTreeWalker(contentEl, NodeFilter.SHOW_TEXT, null);
                    let node: Text | null;
                    const target = comment.selectedText.trim().slice(0, 60);
                    if (!target) return;
                    while ((node = walker.nextNode() as Text | null)) {
                        const idx = node.textContent?.indexOf(target) ?? -1;
                        if (idx !== -1) {
                            try {
                                const range = document.createRange();
                                range.setStart(node, idx);
                                range.setEnd(node, idx + target.length);
                                const mark = document.createElement('span');
                                mark.className = 'comment-mark';
                                mark.dataset.commentId = comment.id;
                                range.surroundContents(mark);
                                mark.addEventListener('click', () => setActiveCommentId(comment.id));
                            } catch { /* range cruzou elementos */ }
                            break;
                        }
                    }
                });
            window.dispatchEvent(new CustomEvent('comment-marks-applied'));
        }, 450);
        return () => clearTimeout(t);
    }, [annotations.highlights, pageId, contentRef, annotations.comments]);

    // ✅ Handler: clique em mark de comentário → ativar card
    useEffect(() => {
        const handleMarkClick = (e: MouseEvent) => {
            const mark = (e.target as HTMLElement).closest('[data-comment-id]') as HTMLElement;
            if (mark) {
                setActiveCommentId(mark.dataset.commentId || null);
            }
        };
        document.addEventListener('click', handleMarkClick);
        return () => document.removeEventListener('click', handleMarkClick);
    }, []);

    // ✅ Handler: Clicar em grifo para mostrar ícone de comentário
    const handleHighlightClick = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target.closest('.highlight-comment-icon')) {
            return;
        }

        if (isRemoving) return;

        // ✅ Mobile: ignorar se o floating btn de grifo/nota estiver visível
        if (document.querySelector('.mobile-highlight-floating-btn')) {
            return;
        }

        const mark = target.closest('[data-highlight-id]') as HTMLElement;

        if (mark) {
            const highlightId = mark.dataset.highlightId;
            if (highlightId) {
                setCommentHighlightId(highlightId);
            }
        } else {
            setCommentHighlightId(null);
        }
    }, [isRemoving]);

    React.useEffect(() => {
        document.addEventListener('click', handleHighlightClick);
        return () => document.removeEventListener('click', handleHighlightClick);
    }, [handleHighlightClick]);

    return (
        <>
            {/* ✅ TOOLBAR FIXA */}
            <UnifiedAnnotationToolbar
                isHighlighting={isHighlighting}
                onToggleHighlighting={() => {
                    setIsHighlighting(!isHighlighting);
                    if (!isHighlighting) {
                        setIsRemoving(false);
                        setActiveNoteCategory(null);
                        setIsCommenting(false);
                    }
                }}
                isRemoving={isRemoving}
                onToggleRemoving={() => {
                    setIsRemoving(!isRemoving);
                    if (!isRemoving) {
                        setIsHighlighting(false);
                        setActiveNoteCategory(null);
                        setIsCommenting(false);
                    }
                }}
                selectedColor={currentColor}
                onColorChange={setCurrentColor}
                selectedThickness={currentThickness}
                onThicknessChange={setCurrentThickness}
                selectedStyle={currentStyle}
                onStyleChange={setCurrentStyle}
                activeNoteCategory={activeNoteCategory}
                onToggleNoteCategory={(cat) => {
                    handleToggleNoteCategory(cat);
                    setIsCommenting(false);
                }}
                onCreateFreeNote={handleCreateFreeNote}
                isCommenting={isCommenting}
                onToggleCommenting={() => {
                    const next = !isCommenting;
                    setIsCommenting(next);
                    if (next) {
                        setIsHighlighting(false);
                        setIsRemoving(false);
                        setActiveNoteCategory(null);
                    }
                }}
                commentCount={(annotations.comments || []).filter(c => c.pageId === pageId).length}
                showComments={showComments}
                onToggleShowComments={() => setShowComments(prev => !prev)}
                onSave={async () => {
                    const result = await onSave();
                    if (result) {
                        setSaveStatus({
                            message: result.message,
                            type: result.success ? 'success' : 'error'
                        });
                        if (result.success) {
                            setIsDirty(false);
                            if (onDirtyChange) {
                                onDirtyChange(false);
                            }
                        }
                        setTimeout(() => setSaveStatus(null), 3000);
                    }
                }}
                onUndo={onUndo}
                onRedo={onRedo}
                canUndo={canUndo}
                canRedo={canRedo}
                onClose={() => {
                    if (isDirty) {
                        setPendingAction(() => () => {
                            document.body.classList.remove('highlight-mode');
                            document.body.classList.remove('remove-mode');
                            setIsHighlighting(false);
                            setIsRemoving(false);
                            setActiveNoteCategory(null);
                            setIsCommenting(false);
                            setPendingComment(null);
                            onClose();
                        });
                        setShowUnsavedModal(true);
                    } else {
                        document.body.classList.remove('highlight-mode');
                        document.body.classList.remove('remove-mode');
                        setIsHighlighting(false);
                        setIsRemoving(false);
                        setActiveNoteCategory(null);
                        setIsCommenting(false);
                        setPendingComment(null);
                        onClose();
                    }
                }}
                darkMode={darkMode}
            />

            {/* ✅ SIDEBAR DE NOTAS */}
            <NotesSidebar
                notes={annotations.notes}
                onNoteClick={handleNoteClick}
                onStartQuiz={handleStartQuiz}
                onDeleteNote={onRemoveNote}
                darkMode={darkMode}
            />

            {/* ✅ MODAL DE NOTA */}
            {noteModalOpen && (
                <NoteModal
                    note={editingNote}
                    onSave={handleSaveNote}
                    onClose={() => {
                        setNoteModalOpen(false);
                        setEditingNote(undefined);
                    }}
                    darkMode={darkMode}
                />
            )}

            {/* ✅ MODAL DE QUIZ */}
            {quizModalOpen && (
                <NoteQuizModal
                    notes={annotations.notes.filter(n => n.pageId === pageId && n.question)}
                    onClose={() => setQuizModalOpen(false)}
                    darkMode={darkMode}
                />
            )}

            {/* ✅ ÍCONE ATIVO (clicado) */}
            {commentHighlightId && (
                <HighlightCommentIcon
                    highlightId={commentHighlightId}
                    existingComment={annotations.highlights.find(h => h.id === commentHighlightId)?.note}
                    onSave={(comment) => {
                        onUpdateHighlightNote(commentHighlightId, comment);
                        setCommentHighlightId(null);
                    }}
                    onClose={() => setCommentHighlightId(null)}
                    darkMode={darkMode}
                />
            )}

            {/* ✅ Toast */}
            {saveStatus && (
                <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 10000 }}>
                    <div className={`toast toast-${saveStatus.type}`}>
                        <span className="toast-icon">
                            {saveStatus.type === 'success'
                                ? <CheckCircle size={16} />
                                : <XCircle size={16} />
                            }
                        </span>
                        <span className="toast-message">{saveStatus.message}</span>
                    </div>
                </div>
            )}

            {/* ✅ COMENTÁRIOS NA MARGEM */}
            {contentRef && showComments && (
                <CommentsMargin
                    comments={annotations.comments || []}
                    pageId={pageId}
                    onRemoveComment={handleRemoveComment}
                    onUpdateComment={handleUpdateComment}
                    activeCommentId={activeCommentId}
                    onSetActiveComment={setActiveCommentId}
                    darkMode={darkMode}
                    contentRef={contentRef}
                />
            )}

            {/* ✅ MODAL PARA ESCREVER COMENTÁRIO */}
            {pendingComment && (
                <CommentInputModal
                    position={pendingComment.position}
                    selectedText={pendingComment.selectedText}
                    onSave={handleSaveComment}
                    onCancel={() => {
                        setPendingComment(null);
                        window.getSelection()?.removeAllRanges();
                    }}
                    darkMode={darkMode}
                />
            )}

            {/* ✅ MODAL DE ALTERAÇÕES NÃO SALVAS */}
            <UnsavedChangesModal
                isOpen={showUnsavedModal}
                onSave={async () => {
                    const result = await onSave();
                    if (result.success) {
                        setIsDirty(false);

                        if (onDirtyChange) {
                            onDirtyChange(false);
                        }
                    }
                    setShowUnsavedModal(false);
                    if (pendingAction) {
                        pendingAction();
                        setPendingAction(null);
                    }
                }
                }
                onDiscard={() => {
                    setIsDirty(false);
                    setShowUnsavedModal(false);
                    if (pendingAction) {
                        pendingAction();
                        setPendingAction(null);
                    }
                }}
                onCancel={() => {
                    setShowUnsavedModal(false);
                    setPendingAction(null);
                }}
                darkMode={darkMode}
            />
        </>
    );
};

export default AnnotationLayer;